import React from 'react';
import {
  Box,
  Typography,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Update as UpdateIcon,
  Warning as WarningIcon,
  TableChart as TableIcon
} from '@mui/icons-material';

interface CompareSummaryProps {
  summary: {
    totalRecords: number;
    newRecords: number;
    existingRecords: number;
    conflicts: number;
  };
  onFilterChange?: (filterType: 'current' | 'incoming' | 'new' | 'conflicted') => void;
  activeFilter?: 'current' | 'incoming' | 'new' | 'conflicted';
  isInAIAvatarLayout?: boolean;
}

const CompareSummary: React.FC<CompareSummaryProps> = ({ summary, onFilterChange, activeFilter, isInAIAvatarLayout = false }) => {
  const theme = useTheme();

  const getSummaryColors = () => {
    if (isInAIAvatarLayout) {
      const baseColor = theme.palette.mode === 'light' ? '#000000' : '#ffffff';
      const bgColor = theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)';
      return {
        incoming: { color: baseColor, bgColor, textColor: baseColor },
        current: { color: baseColor, bgColor, textColor: baseColor },
        new: { color: baseColor, bgColor, textColor: baseColor },
        conflicted: { color: baseColor, bgColor, textColor: baseColor }
      };
    }
    return {
      incoming: { color: '#7b1fa2', bgColor: 'rgba(123, 31, 162, 0.08)', textColor: '#7b1fa2' },
      current: { color: '#1976d2', bgColor: 'rgba(25, 118, 210, 0.08)', textColor: '#1976d2' },
      new: { color: '#2e7d32', bgColor: 'rgba(46, 125, 50, 0.08)', textColor: '#2e7d32' },
      conflicted: { color: '#ed6c02', bgColor: 'rgba(237, 108, 2, 0.08)', textColor: '#ed6c02' }
    };
  };

  const colors = getSummaryColors();

  const summaryItems = [
    {
      title: 'Incoming Records',
      value: summary.totalRecords, // Total records in incoming data
      icon: <TableIcon />,
      ...colors.incoming,
      filterType: 'incoming' as const
    },
    {
      title: 'Current Records',
      value: summary.existingRecords,
      icon: <UpdateIcon />,
      ...colors.current,
      filterType: 'current' as const
    },
    {
      title: 'New Records',
      value: summary.newRecords,
      icon: <AddIcon />,
      ...colors.new,
      filterType: 'new' as const
    },
    {
      title: 'Conflicted Records',
      value: summary.conflicts,
      icon: <WarningIcon />,
      ...colors.conflicted,
      filterType: 'conflicted' as const
    }
  ];

  // Filter out items with 0 values - only show filters that have data
  const filteredSummaryItems = summaryItems.filter(item => item.value > 0);

  return (
    <Box sx={{ 
      display: 'flex', 
      gap: 1.5, 
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'flex-start'
    }}>
      {filteredSummaryItems.map((item, index) => (
        <Box
          key={index}
          onClick={() => onFilterChange?.(item.filterType)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            backgroundColor: activeFilter === item.filterType ? `${item.color}15` : item.bgColor,
            color: item.textColor,
            borderRadius: 2,
            border: `1px solid ${activeFilter === item.filterType ? item.color : `${item.color}30`}`,
            transition: 'all 0.15s ease',
            cursor: onFilterChange ? 'pointer' : 'default',
            minWidth: 'fit-content',
            '&:hover': {
              backgroundColor: `${item.color}12`,
              borderColor: item.color,
              transform: 'translateY(-1px)'
            }
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            fontSize: '1.1rem'
          }}>
            {item.icon}
          </Box>
          <Typography variant="body2" sx={{ 
            fontWeight: 600,
            fontSize: '0.85rem',
            whiteSpace: 'nowrap'
          }}>
            {item.value} {item.title}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default CompareSummary;
